import { useState, useMemo, useRef } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { useAuth } from '@/auth'
import { SavedHeader, SavedSearch, SavedButUnanalyzed, SavedInfluencerCard } from '@/components/influencers'
import { QuickActions } from '@/components/influencers/quick-actions'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

import { Skeleton } from '@/components/ui/skeleton'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { useToast } from '@/hooks'
import { instagramService } from '@/services'
import { Loader2, AlertCircle, RefreshCw, BarChart3 } from 'lucide-react'

export function InfluencersPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const isLoading = authLoading
  const error = null as any
  const refetch = () => {}
  const { toast } = useToast()
  const [searchQuery, setSearchQuery] = useState('')
  const latestQueryRef = useRef('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    analyticsId: string
    username: string
  }>({
    open: false,
    analyticsId: '',
    username: ''
  })

  // Header stats via lightweight summary API
  const { data: summaryData } = useQuery({
    queryKey: ['account-summary'],
    queryFn: () => instagramService.getAccountSummary(),
    enabled: isAuthenticated && !authLoading,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const stats = useMemo(() => {
    const s = summaryData?.summary
    if (!s) return { totalInfluencers: 0, totalFollowers: 0, averageEngagement: 0, lastAdded: null as string | null }
    return {
      totalInfluencers: s.total_influencers,
      totalFollowers: s.total_followers,
      averageEngagement: s.average_days_analyzed,
      lastAdded: s.last_added || null,
    }
  }, [summaryData])

  // Delete dialog is opened from inline actions in future; keep state for confirmation component

  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      analyticsId: '',
      username: ''
    })
  }

  const handleDeleteAnalyticsRecord = async () => {
    const { analyticsId, username } = deleteDialog
    setDeletingId(analyticsId)
    
    try {
      const result = await instagramService.deleteAnalyticsRecord(analyticsId)
      if (result.success) {
        toast({
          title: "Analytics record deleted",
          description: `Analytics for "${username}" has been successfully deleted.`,
          variant: "default",
        })
        await refetch()
        closeDeleteDialog()
      } else {
        toast({
          title: "Error",
          description: "Failed to delete analytics record. Please try again.",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error deleting analytics record:', error)
      toast({
        title: "Error",
        description: "Failed to delete analytics record. Please try again.",
        variant: "destructive",
      })
    } finally {
      setDeletingId(null)
    }
  }

  // Removed unused remove handler (legacy list)

  // Using inline handlers below

  // Using inline handlers below

  // Paged analyzed accounts with server-side partial search
  const {
    data: paged,
    isLoading: pagedLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchPaged,
  } = useInfiniteQuery<{ success: boolean; total_batch: number; profiles: any[]; next_cursor?: string | null }, Error, any, [string, string], string | null>({
    queryKey: ['saved-analytics-paged', searchQuery],
    queryFn: async ({ pageParam, signal }) => {
      // Cancel out-of-date requests: only proceed if query matches latest ref
      if (latestQueryRef.current !== searchQuery) {
        throw new Error('stale')
      }
      return instagramService.getSavedAnalyticsPaged({ limit: 12, cursor: (pageParam as string | null) ?? null, q: searchQuery || null }, { signal })
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => (lastPage?.next_cursor ?? undefined),
    enabled: isAuthenticated && !authLoading,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const pagedProfiles = (paged?.pages || []).flatMap((p: any) => p.profiles || [])
  
  // Debug: Log the actual API response
  console.log('Paged API response:', {
    pages: paged?.pages?.map((p: any) => ({ 
      total_batch: p.total_batch, 
      profiles_count: p.profiles?.length, 
      next_cursor: p.next_cursor 
    })),
    totalProfiles: pagedProfiles.length,
    hasNextPage,
    firstPageResponse: paged?.pages?.[0]
  })

  // Full page skeleton during initial load or pending auth
  if (isLoading || authLoading || !isAuthenticated) {
    return (
      <div className="space-y-4 min-h-[70vh]">
        {/* Header skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-[200px]" />
                      <div className="flex items-center gap-4">
                        <Skeleton className="h-3 w-[60px]" />
                        <Skeleton className="h-3 w-[80px]" />
                        <Skeleton className="h-3 w-[70px]" />
                      </div>
                    </div>
                  </div>
                  <Skeleton className="h-4 w-4" />
                </div>
                <Skeleton className="h-3 w-full mt-2" />
                <Skeleton className="h-3 w-3/4" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex gap-2">
                  <Skeleton className="h-8 flex-1" />
                  <Skeleton className="h-8 flex-1" />
                  <Skeleton className="h-8 flex-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-4">
        <SavedHeader />
        <Card>
          <CardContent className="text-center py-12 space-y-3">
            <div className="p-3 rounded-lg bg-destructive/10 w-fit mx-auto">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <div>
              <h3 className="font-semibold">Failed to load analytics</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {error}
              </p>
              <Button onClick={refetch} variant="outline" size="sm">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <SavedHeader 
        totalInfluencers={stats.totalInfluencers}
        totalFollowers={stats.totalFollowers}
        averageEngagement={stats.averageEngagement}
        lastAdded={stats.lastAdded || undefined}
      />
      
      <QuickActions hasInfluencers={(stats.totalInfluencers || 0) > 0} />
      
      
      
        <SavedSearch 
        searchQuery={searchQuery}
        onSearchChange={(q) => {
          latestQueryRef.current = q
          setSearchQuery(q)
          refetchPaged()
        }}
        onClearSearch={() => {
          latestQueryRef.current = ''
          setSearchQuery('')
          refetchPaged()
        }}
      />

                  {/* Paged analyzed profiles */}
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-50/50 dark:bg-green-950/20 flex-shrink-0">
                    <BarChart3 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg font-semibold">Analyzed Profiles</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <CardDescription>
                            Filter by username, newest first in storage order
                          </CardDescription>
                          {pagedProfiles.length > 0 && (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                              {pagedProfiles.length}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {hasNextPage && (
                          <Button variant="outline" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                            {isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load More'}
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => refetchPaged()} disabled={pagedLoading}>
                          {pagedLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
            {pagedProfiles.length === 0 && !pagedLoading && (
              <div className="text-sm text-muted-foreground">No profiles yet.</div>
            )}
            {pagedProfiles.map((profile: any) => {
              const username = profile.target_username || profile.profile_overview?.username
              const fullName = profile.profile_overview?.full_name || username
              const followers = profile.profile_overview?.followers || 0
              
              return (
                <SavedInfluencerCard
                  key={profile.id}
                  id={profile.id}
                  title={fullName}
                  snippet={`@${username} • Instagram Profile`}
                  followers={followers}
                  url={`https://instagram.com/${username}`}
                  savedDate={profile.processing_timestamp}
                  daysAnalyzed={profile.days_analyzed}
                  isAnalyzed={true}
                  onDelete={() => {
                    setDeleteDialog({
                      open: true,
                      analyticsId: profile.id,
                      username: username
                    })
                  }}
                />
              )
            })}

          </div>
        </CardContent>
      </Card>
      
      <SavedButUnanalyzed />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialog.open}
        onOpenChange={closeDeleteDialog}
        title="Delete Analytics Record"
        description={`Are you sure you want to delete the analytics for "${deleteDialog.username}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="destructive"
        onConfirm={handleDeleteAnalyticsRecord}
        loading={deletingId === deleteDialog.analyticsId}
      />
    </div>
  )
} 